import React, {useEffect, useMemo} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import {FormControl, FormLabel, Grid, MenuItem, Select} from '@mui/material';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import {useSelector} from 'react-redux';
import moment from 'moment';
import uuidv4 from 'uuid/v4';
import {user} from '../../../../store/reducers/user';
import {formGroup, mappers, outcomes, outcomesTexts, VteGroupsSettings} from './vte_data';
import * as _cloneDeep from 'lodash/cloneDeep';
import * as _merge from 'lodash/merge';
import Formatter from '../../../../util/formatter';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import {getOutcomeView} from './vteUtils';
import AgyleCheckBox from '../../../../components/ui/checkbox/agyleCheckBox';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paperDarker,
        padding: 15,
        borderRadius: 10,
    },
    selectorItemFormControl: {
        width: '30%',
    },
    modalFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 5,
    },
    controlButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        minWidth: '250px',
    },
    modalHeader: {
        height: 100,
        position: 'relative',
        top: 0,
    },
    modalBody: {
        position: 'relative',
        height: 'calc(100% - 200px)',
        overflow: 'auto',
        backgroundColor: theme.palette.background.paperDarker,
        borderRadius: 10,
        marginTop: 35,
    },
    stepDescription: {
        marginBottom: 0,
        paddingLeft: 20,
        paddingRight: 20,
        minHeight: 50,
        position: 'relative',
        borderBottom: '5px solid ' + theme.palette.background.field,
        padding: 10,
        backgroundColor: theme.palette.background.paperDarker,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: 'bold',
    },
    stepCount: {
        fontWeight: 'bold',
    },
    qestItem: {
        padding: 3,
    },
    blockSwitchActions: {
        paddingLeft: '30px',
        minHeight: '48px',
        paddingTop: '10px',
        flexBasis: 'content',
        paddingRight: '30px',
    },
    icon: {
        fontSize: 'large',
    },
    step: {
        color: 'gray',
        fontSize: 'medium',
    },
    formView: {
        fontSize: 13,
        padding: 20,
        backgroundColor: theme.palette.background.paperDarker,
        borderRadius: 10,
    },
    titleLabel: {
        padding: '0 5px 5px',
        fontWeight: 'bold',
        fontSize: '15px',
        borderBottom: '1px solid ' + theme.palette.divider,
    },
}));

function VteForm(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const [activeStep, setActiveStep] = React.useState(0);

    const [type, setType] = React.useState('');
    const [subType, setSubType] = React.useState('');

    const [questions, setQuestions] = React.useState(null);
    const [questionValues, setQuestionValues] = React.useState(null);

    const [riskFactorMap, setRiskFactorMap] = React.useState({});
    const [secondSetMap, setSecondSetMap] = React.useState({});

    const [outcomeMatrix] = React.useState(outcomes);
    const [formSettings, setFormSettings] = React.useState(null);

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const [form, setForm] = React.useState({
        __id: uuidv4() + '',
        name: 'VTE',

        type: type,
        subType: subType,
        outcome: null,
        note: '',

        createdWho: tester,
        createdWhoUserName: userObject.firstName,
        createdWhen: moment().toISOString(),
        modifiedWho: tester,
        modifiedWhoUserName: userObject.firstName,
        modifiedWhen: moment().toISOString(),

        patientId: props.patient.patient.__id,
        attendanceId: props.patient?.admission?.__id,
        visitNumber: props.patient?.admission?.attendanceId,
    });
    const [revalidate, setRevalidate] = React.useState(true);

    useEffect(() => {
        if (props.formToEdit) {
            setType(props.formToEdit.type);
            setSubType(props.formToEdit.subType);
        }
    }, [props.formToEdit]);

    useEffect(() => {
        props.disableSubmit(activeStep !== 4);
        if (activeStep === 4 && revalidate) {
            setRevalidate(false);
            props.onChange(getOutcome());
        } else if (activeStep !== 4 && !revalidate) {
            setRevalidate(true);
        }
    }, [activeStep, revalidate, type, form]);

    useEffect(() => {
        setForm(prevState => ({
            ...prevState,
            type: type || '',
            subType: subType || '',
        }));
    }, [type, subType]);

    const handlePrev = () => {
        if (activeStep > 1)
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const importForm = () => {
        let value, questions = null;
        // eslint-disable-next-line default-case
        switch (formGroup[type]) {
            case 'vte':
                const {vteValues, vteQuestions, bleedingRiskQuestions} = require('./vte_data');
                value = vteValues;
                questions = {assessments: vteQuestions, bleedingRisk: bleedingRiskQuestions};

                break;
            case 'thromboembolism':
                const {
                    LimbPatientValues,
                    limbPatientQuestions,
                    limbPatientValuesQuestions,
                    limbPatientOtherQuestions,
                } = require('./vte_data');
                value = LimbPatientValues;
                questions = {
                    riskFactors: limbPatientQuestions,
                    contraindications: limbPatientValuesQuestions,
                    other: limbPatientOtherQuestions,
                };
                break;
        }
        return {value, questions};

    };

    const openForm = () => {
        setRiskFactorMap(mappers[type]);
        setSecondSetMap(formGroup[type] !== 'thromboembolism' ? mappers.bleedingRiskMap : mappers.contraindicationsRiskMap);

        let importedValues = importForm();

        setQuestions(_cloneDeep(importedValues.questions));
        setQuestionValues(_cloneDeep(importedValues.value));

        if (props.formToEdit) {
            setQuestionValues(_merge({}, _cloneDeep(importedValues.value), props.formToEdit.values));
        }
    };

    const changeValue = (key, questionSet, value) => {
        let newValueSets = {...questionValues};
        newValueSets[questionSet][key] = value !== null ? value : !newValueSets[questionSet][key];
        setQuestionValues(newValueSets);
    };

    const patientOver60 = useMemo(() => {
        return moment().diff(moment(props.patient.patient.dob), 'years') >= 60;
    }, [props.patient]);

    const functions = {
        patientOver60: patientOver60,
    };

    const questionVisible = (keyObj, values, questionSet, question) => {
        let visible1, visible2, visible3, visible4 = false;

        if (question[keyObj].actions.visibleOnyIfTypeIs)
            question[keyObj].actions.visibleOnyIfTypeIs.forEach(typeIs => {
                if (!visible1)
                    visible1 = typeIs === type;
            });

        if (question[keyObj].actions.visibleOnyIfSubTypeIs)
            visible2 = question[keyObj].actions.visibleOnyIfSubTypeIs === subType;

        if (question[keyObj].actions.visibleOnyIfValueIsTrue) {
            if (values[question[keyObj].actions.visibleOnyIfValueIsTrue]) {
                visible3 = true;
            } else {
                if (values[keyObj]) changeValue(keyObj, questionSet, false);
            }
        }

        if (question[keyObj].actions.valueFromFunction)
            visible4 = true;

        return visible1 || visible2 || visible3 || visible4;

    };

    const getQuestions = (questionSet) => {
        let html = [];

        let question = questions[questionSet];
        let values = questionValues[questionSet];
        const isQuestionsOpened = steps[activeStep].label === 'Questions';

        Object.keys(values).map((keyObj) => {
            let haveValue = question[keyObj].actions?.hasOwnProperty('valueFromFunction');
            if (haveValue && functions[question[keyObj].actions.valueFromFunction] !== values[keyObj])
                changeValue(keyObj, questionSet, functions[question[keyObj].actions.valueFromFunction]);

            let item =
                <Grid
                    container
                    key={keyObj}
                    alignItems="center"
                    justifyContent={isQuestionsOpened && 'space-between'}
                    className={classes.qestItem}
                >
                    {isQuestionsOpened ?
                        <>
                            <Grid item xs={10} style={{paddingLeft: '30px', minHeight: '48px', paddingTop: '10px'}}>
                                {question[keyObj].text}
                            </Grid>

                            <Grid item xs={2} className={classes.blockSwitchActions}>
                                <AgyleBlockSwitch
                                    onChange={() => changeValue(keyObj, questionSet, null)}
                                    value={values[keyObj]}
                                />
                            </Grid>
                        </>
                        :
                        <>
                            <Grid item xs={1} style={{textAlign: 'center'}}>
                                <AgyleCheckBox
                                    value={values[keyObj]}
                                    disabled={haveValue}
                                    onChange={() => changeValue(keyObj, questionSet, null)}
                                    inputProps={{'aria-label': 'primary checkbox'}}
                                />
                            </Grid>

                            <Grid item xs={11} onClick={() => changeValue(keyObj, questionSet, null)}>
                                {question[keyObj].text}
                            </Grid>
                        </>
                    }
                </Grid>;
            if (!question[keyObj].actions || (question[keyObj].actions && questionVisible(keyObj, values, questionSet, question)))
                html.push(item);
        });
        return html;
    };

    const findOutcomeMapKeyForFirstSet = (questionSet) => {
        let mapKey = [];

        let someRiskFactorCheck = Object.keys(questionValues[questionSet]).some(key => {
            return riskFactorMap.fieldToCheck?.some(field => field.field === key) ? false : questionValues[questionSet][key];
        });

        mapKey.push(someRiskFactorCheck ? riskFactorMap.outcome : 'O' + riskFactorMap.outcome);

        if (subType) mapKey.push(subType);

        riskFactorMap.fieldToCheck?.forEach((value) => {
            if (questionValues[questionSet][value.field]) mapKey.push(value.value);
        });

        mapKey = mapKey.join('_').toString();

        return riskFactorMap.map[mapKey];
    };

    const findOutcomeMapKeyForSecondSet = (questionSet) => {
        return Object.keys(questionValues[questionSet]).reduce((reduce, key) => {
            return secondSetMap.map[questionValues[questionSet][key] ? questions[questionSet][key].outcome : 0] > reduce ?
                secondSetMap.map[questionValues[questionSet][key] ? questions[questionSet][key].outcome : 0] : reduce;
        }, 0);
    };

    const saveOutcomeTextToForm = (val) => {
        let index = outcomeMatrix[type][findOutcomeMapKeyForFirstSet(formSettings.components[0].set)][findOutcomeMapKeyForSecondSet(formSettings.components[1].set)];
        return {
            ...val,
            outcome: outcomesTexts[index],
        };
    };

    const saveComponentTextsAndValuesToForm = () => {
        let formLocal = _cloneDeep(form);
        formLocal.values = {};
        Object.keys(questionValues).forEach((set, index) => {
            formLocal.values[set] = {};
            formLocal[set + 'Texts'] = {};

            Object.keys(questionValues[set]).forEach((key, index) => {
                if (questionValues[set][key] || VteGroupsSettings[formGroup[type]].components.find((item) => item.set === set).saveAllValues) {
                    formLocal.values[set][key] = questionValues[set][key];
                    formLocal[set + 'Texts'][key] = questions[set][key].text;
                }
            });
        });
        return formLocal;
    };

    const createOutcome = () => {
        const val = saveComponentTextsAndValuesToForm();
        return saveOutcomeTextToForm(val);
    };

    const getOutcome = () => {
        const getOutcome = createOutcome();
        setForm(getOutcome);
        return getOutcome;
    };

    const handleChangeType = (event) => {
        setType(event.target.value);
    };

    const handleChangeSubType = (event) => {
        setSubType(event.target.value);
    };

    const steps = useMemo(() => {
        let stepsTmp = [];

        stepsTmp.push({
            label: 'Identify Patient Group',
            component: 'G',
        });
        if (formSettings?.components) {
            stepsTmp = stepsTmp.concat(formSettings.components);
            stepsTmp.push({
                label: 'Outcome',
                component: 'O',
            });
        }
        return stepsTmp;
    }, [formSettings]);

    useEffect(() => {
        if (type) {
            setFormSettings(VteGroupsSettings[formGroup[type]]);
            openForm();
        }
    }, [type, subType]);

    return (
        <>
            <div className={classes.vteHeader}>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    // nonLinear
                    sx={{
                        '.MuiStepConnector-root span': {
                            margin: 5,
                        },
                        '.MuiSvgIcon-root': {
                            borderRadius: '50%',
                            color: 'grey',
                            fontSize: 45,
                        },
                        '.MuiStepIcon-text': {
                            fontWeight: 500,
                        },
                        '.MuiSvgIcon-root.Mui-active': {
                            color: '#1976d2',
                            padding: '3px',
                            borderRadius: '50%',
                            border: '1px solid #1976d2',
                            marginY: '-3px',
                        },
                        '.Mui-active .MuiStepIcon-text': {
                            fill: 'white',
                        },
                        '.MuiStepLabel-labelContainer span': {
                            fontSize: 'large',
                        },
                    }}
                >
                    {steps.map((item, index) => {
                        const stepProps = {};
                        const labelProps = {};
                        return (
                            <Step key={index + 'l'} {...stepProps}>
                                <StepLabel
                                    classes={{label: classes.icon}}
                                    {...labelProps}
                                >
                                    <div className={classes.step}>
                                        {t('STEP')} {index + 1}
                                    </div>

                                    {item.label}
                                </StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
            </div>

            <div className={classes.modalBody + ' scroll-normal-white'}>
                <div className={classes.stepDescription}>
                    {steps[activeStep].component === 'G' ? 'Introduction' : steps[activeStep].label}
                    {steps.length > 2 &&
                        <div className={classes.stepCount}>
                            {activeStep + 1} / {steps.length}
                        </div>
                    }
                </div>

                {steps[activeStep].component === 'G' &&
                    <div style={{padding: 10}}>
                        <FormLabel style={{fontWeight: 'bold'}}>
                            {t('Select one of the following patient groups:')}
                        </FormLabel>

                        <div className={classes.formItem} style={{marginLeft: 20}}>
                            <RadioGroup
                                row
                                name="patient-groups"
                                value={type}
                                onChange={handleChangeType}
                            >
                                <FormControlLabel
                                    value="lowerLimb"
                                    control={<Radio disabled={!!props.formToEdit} color="primary"/>}
                                    label={t('Lower limb/foot & ankle venous thromboembolism (VTE) prophylaxis. For all adult patients (>18yrs) with lower limb injury or surgery with an anticipated reduction in mobility.')}
                                    labelPlacement="end"
                                />
                            </RadioGroup>
                        </div>

                        {type === 'orthopaedicPatient' &&
                            <FormControl className={classes.selectorItemFormControl}>
                                <h4>List:</h4>

                                <Select
                                    className={classes.dropDown}
                                    labelId="label"
                                    id="select"
                                    disabled={props.formToEdit}
                                    value={subType}
                                    inputProps={{
                                        name: 'Select one of the following patient groups:',
                                        id: 'age-native-helper',
                                    }}
                                    onChange={handleChangeSubType}
                                    classes={{icon: classes.dropDownIcon}}
                                >
                                    <MenuItem value="EK">
                                        {t('Elective knee replacement Patient')}
                                    </MenuItem>

                                    <MenuItem value="EH">
                                        {t('Elective hip replacement Patient')}
                                    </MenuItem>

                                    <MenuItem value="HF">
                                        {t('Hip fracture Patient')}
                                    </MenuItem>

                                    <MenuItem value="OO">
                                        {t('Other orthopaedic Patient')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        }
                    </div>
                }
                {steps[activeStep].component !== 'O' && steps[activeStep].component !== 'G' && getQuestions(steps[activeStep].set)}
                {(steps[activeStep].component === 'O' && !revalidate) && getOutcomeView(form, classes, t)}
            </div>

            <div className={classes.modalFooter}>
                {activeStep > 1 &&
                    <AgyleButton
                        variant="contained"
                        color="primary"
                        onClick={handlePrev}
                        fullWidth
                    >
                        {t('Back to Step')}{` ${activeStep}`}
                    </AgyleButton>
                }

                {steps[activeStep].component !== 'O' &&
                    <AgyleButton
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={(activeStep === 0 && (type === '' || (type === 'orthopaedicPatient' && subType === ''))) || props.disableSave}
                        fullWidth
                    >
                        {t('Next Step')}
                    </AgyleButton>
                }
            </div>
        </>
    );
}

export default VteForm;
